'use client';
import { Box, IconButton } from '@mui/material';
import { FC, MouseEventHandler, Ref } from 'react';

import {
  AbgTypography,
  Caret,
  formatMoney,
  mergeSx,
  mergeSxWithStylePart,
} from '@abg/kit-base';

import { useIsFlexPayEnabled } from '../../../../shared/hooks/use-is-flex-pay-enabled';

import { NOT_AVAILABLE } from './total-amount.constants';
import { TotalAmountProps } from './total-amount.types';
import {
  checkoutMobileTotalLabelStyling,
  checkoutMobileWrapperStyling,
  priceDisclaimer,
  totalAmountPriceStyling,
  totalAmountStyling,
  totalAmountTextStyling,
  totalAmountWrapperBoxStyling,
  totalAmountWrapperStyling,
} from './TotalAmount.styling';

export const TotalAmount: FC<TotalAmountProps> = ({
  totalText,
  amount,
  currencyCode,
  isPopOverOpen,
  onPopOverClick,
  styleParts,
  caretStyles = {},
  isCheckoutMobile,
  isModificationFlow,
  priceDisclaimerLabel,
  toggleRef,
}) => {
  const isFlexPayEnabled = useIsFlexPayEnabled();

  const onClick: MouseEventHandler<HTMLElement> = () => {
    onPopOverClick?.(!isPopOverOpen);
  };

  const rotationDegree = isPopOverOpen ? 180 : 0;
  const totalAmount = amount ?? NOT_AVAILABLE;
  const isExpandable = totalAmount !== NOT_AVAILABLE && !isCheckoutMobile;

  return (
    <Box
      sx={
        isCheckoutMobile
          ? checkoutMobileWrapperStyling
          : totalAmountWrapperStyling(isModificationFlow)
      }
    >
      <Box sx={totalAmountWrapperBoxStyling}>
        <AbgTypography
          data-testid="action-footer-total-label"
          variant="bodySmallMedium"
          sx={
            isCheckoutMobile
              ? checkoutMobileTotalLabelStyling
              : mergeSxWithStylePart(
                  totalAmountTextStyling,
                  styleParts,
                  'totalTex',
                )
          }
        >
          {totalText}
        </AbgTypography>

        <Box
          sx={totalAmountStyling.amountWrapper}
          ref={toggleRef as Ref<HTMLDivElement>}
        >
          <AbgTypography
            data-testid="action-footer-total-amount"
            variant="h5"
            className={isFlexPayEnabled ? 'fxp-footer-total' : ''}
            sx={mergeSxWithStylePart(
              totalAmountPriceStyling,
              styleParts,
              'totalPrice',
            )}
            onClick={isExpandable ? onClick : undefined}
          >
            {formatMoney(Number(totalAmount), {
              currency: String(currencyCode),
              includeCents: true,
            })}
          </AbgTypography>

          {isExpandable && (
            <IconButton
              sx={totalAmountStyling.carretButton}
              aria-label="Price summary"
              onClick={onClick}
            >
              <Caret
                sx={mergeSx(totalAmountStyling.caretIcon, {
                  rotate: `${rotationDegree}deg`,
                  ...caretStyles,
                })}
                data-testid="total-price-icon"
              />
            </IconButton>
          )}
        </Box>
      </Box>
      {/* Price Disclaimer  */}
      {priceDisclaimerLabel && (
        <AbgTypography sx={priceDisclaimer}>
          {priceDisclaimerLabel}
        </AbgTypography>
      )}
    </Box>
  );
};
